import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Link,
  Pagination,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import MemoryIcon from '@mui/icons-material/Memory';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageArea from '../shared/PageArea';
import PageTitle from '../shared/PageTitle';
import LoadingWheel from '../shared/LoadingWheel';
import MetricCard from '../shared/MetricCard';
import { ProcessingJobItem, ProcessingJobsResult } from '../../web-api-client';
import { authenticatedFetch, getApiBaseUrl } from '../../api/apiConfig';
import { usePermissions, Permissions } from '../../contexts/PermissionContext';
import { canRetryJob, pipelineStageKey } from '../shared/pollGuard';
import AiCostsPanel from './AiCostsPanel';
import ExtractionSettingsPanel from './ExtractionSettingsPanel';
import PipelineStepper, { pipelineState } from '../shared/PipelineStepper';

const ProcessingPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const canRecover = hasPermission(Permissions.OPS_RECOVER);

  const [data, setData] = useState<ProcessingJobsResult | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [retryError, setRetryError] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const requestIdRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const query = new URLSearchParams({ pageNumber: String(page), pageSize: '25' });
      const response = await authenticatedFetch.fetch(
        `${getApiBaseUrl() ?? ''}/api/processing/jobs?${query}`,
        { signal: controller.signal },
      );
      if (!response.ok) {
        throw new Error(`Processing jobs request failed (${response.status})`);
      }
      const result = (await response.json()) as ProcessingJobsResult;
      if (requestId === requestIdRef.current) {
        setData(result);
        setLoadError(false);
      }
    } catch (error) {
      if (!controller.signal.aborted && requestId === requestIdRef.current) {
        setLoadError(true);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [page]);

  useEffect(() => {
    let active = true;
    let timer: number | undefined;

    const poll = async () => {
      await load();
      if (active) {
        timer = window.setTimeout(poll, 4000);
      }
    };

    void poll();
    return () => {
      active = false;
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
      abortRef.current?.abort();
    };
  }, [load]);

  const retry = async (job: ProcessingJobItem) => {
    if (!job.jobId) {
      return;
    }
    try {
      setRetryError(false);
      const response = await authenticatedFetch.fetch(`${getApiBaseUrl() ?? ''}/api/processing/jobs/${encodeURIComponent(job.jobId)}/retry`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: t('processing.retryReason') }),
      });
      if (!response.ok) {
        const problem = await response.json().catch(() => ({}));
        throw new Error(problem.detail ?? t('processing.retryFailed', { status: response.status }));
      }
      setMessage(t('processing.retried', { name: job.fileName }));
      load();
    } catch (error) {
      setRetryError(true);
      setMessage(error instanceof Error ? error.message : t('common.error'));
    }
  };

  const failed = (data?.items ?? []).filter(
    (j) => j.jobStatus === 'DeadLettered' || j.jobStatus === 'Failed',
  );

  return (
    <PageArea>
      <PageTitle title={t('processing.title')} subtitle={t('processing.subtitle')} />
      {(canRecover || hasPermission(Permissions.SETTINGS_MANAGE)) && (
        <Accordion TransitionProps={{ timeout: 0, unmountOnExit: true }} disableGutters elevation={0} sx={{ mb: 8, border: 1, borderColor: 'divider', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography sx={{ fontSize: 14, fontWeight: 600 }}>{t('processing.controls')}</Typography></AccordionSummary>
          <AccordionDetails>
            {canRecover && <AiCostsPanel />}
            {hasPermission(Permissions.SETTINGS_MANAGE) && <ExtractionSettingsPanel />}
          </AccordionDetails>
        </Accordion>
      )}
      {message && (
        <Alert severity={retryError ? "error" : "success"} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}
      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={() => void load()}>{t('common.retry')}</Button>}>
          {t('processing.loadError')}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
          gap: 6,
          mb: 8,
        }}
      >
        <MetricCard label={t('processing.inProgress')} value={data?.counts?.inProgress ?? 0} icon={<MemoryIcon />} />
        <MetricCard label={t('processing.awaitingReview')} value={data?.counts?.awaitingReview ?? 0} icon={<WarningAmberIcon />} tone="warning" />
        <MetricCard label={t('processing.failed')} value={data?.counts?.failed ?? 0} icon={<ErrorOutlineIcon />} tone="error" />
        <MetricCard label={t('processing.succeeded')} value={data?.counts?.succeeded ?? 0} icon={<CheckCircleOutlineIcon />} tone="success" />
      </Box>

      {failed.length > 0 && (
        <Box sx={{ mb: 8 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>{t('processing.needsAttention')}</Typography>
          <Box sx={{ display: 'grid', gap: 6, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
            {failed.map((job) => (
              <Paper key={job.jobId} variant="outlined" sx={{ p: 7, borderColor: 'error.light' }}>
                <Typography sx={{ fontWeight: 600 }} noWrap>
                  {job.fileName}
                </Typography>
                <Typography variant="textSM" color="text.secondary">
                  {job.projectInternalReference ?? t('processing.unfiled')} · {t(pipelineStageKey(job.currentStage), { defaultValue: job.currentStage ?? '' })}
                </Typography>
                {job.lastError && (
                  <Typography variant="textSM" color="error" sx={{ mt: 1, display: 'block' }}>
                    {job.lastError}
                  </Typography>
                )}
                {canRecover && canRetryJob(job.jobStatus) && (
                  <Button sx={{ mt: 1 }} variant="secondary" size="md" onClick={() => retry(job)}>
                    {t('processing.retry')}
                  </Button>
                )}
              </Paper>
            ))}
          </Box>
        </Box>
      )}

      {loading && !data ? (
        <LoadingWheel />
      ) : (
        <>
          <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 2 }}>
            <Box sx={{ px: 8, py: 6, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
              <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{t('processing.documents')}</Typography>
            </Box>
            {(data?.items ?? []).length === 0 && <Typography sx={{ p: 3, color: 'text.secondary', fontSize: 14 }}>{t('pipeline.empty')}</Typography>}
            <Box component="ul" sx={{ p: 0, m: 0, listStyle: 'none' }}>
              {(data?.items ?? []).map((job) => (
                <ProcessingJobRow key={job.jobId} job={job} canRecover={canRecover} retry={retry} />
              ))}
            </Box>
          </Paper>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <Pagination
              count={Math.max(1, Math.ceil((data?.totalCount ?? 0) / 25))}
              page={page}
              onChange={(_, value) => setPage(value)}
            />
          </Box>
        </>
      )}
    </PageArea>
  );
};

const ProcessingJobRow: React.FC<{
  job: ProcessingJobItem;
  canRecover: boolean;
  retry: (job: ProcessingJobItem) => Promise<void>;
}> = ({ job, canRecover, retry }) => {
  const { t } = useTranslation();
  return (
    <Box component="li" sx={{ px: 8, py: 7, display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: pipelineState(job.jobStatus, job.documentStatus).complete || pipelineState(job.jobStatus, job.documentStatus).failed ? 'minmax(220px, 1fr) minmax(190px, 260px) 130px' : 'minmax(0, 1fr)' }, gap: { xs: 5, lg: 8 }, alignItems: 'center', borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 }, '&:hover': { bgcolor: 'action.hover' } }}>
      <Stack direction="row" spacing={5} alignItems="center" sx={{ minWidth: 0 }}>
        <Box sx={{ width: 36, height: 40, bgcolor: 'action.hover', borderRadius: 1.5, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><InsertDriveFileOutlinedIcon sx={{ color: 'text.secondary', fontSize: 22 }} /></Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography title={job.fileName} sx={{ fontSize: 14, fontWeight: 600, overflowWrap: 'anywhere' }}>{job.fileName}</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 2 }}>{job.projectInternalReference ?? t('processing.unfiled')}</Typography>
        </Box>
      </Stack>
      <PipelineStepper currentStage={job.currentStage} jobStatus={job.jobStatus} documentStatus={job.documentStatus} />
      <Stack direction="row" justifyContent={{ xs: 'flex-start', lg: 'flex-end' }} spacing={1} sx={{ '&:empty': { display: 'none' } }}>
        {job.documentId && pipelineState(job.jobStatus, job.documentStatus).complete && (
          <Link component={RouterLink} to={`/review/${job.documentId}`} underline="hover" sx={{ fontSize: 13, fontWeight: 600, py: 4 }}>
            {t(job.documentStatus === 'NeedsReview' ? 'common.review' : 'pipeline.viewDocument')}
          </Link>
        )}
        {canRecover && canRetryJob(job.jobStatus) && (
          <Button variant="secondary" size="md" onClick={() => retry(job)}>{t('processing.retry')}</Button>
        )}
      </Stack>
    </Box>
  );
};

export default ProcessingPage;
